import styled from 'styled-components';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { RhythmPattern } from '../../../../../common/src/rhythm_echo.ts';
import { resultColorCss } from './rhythm_colors.ts';
import {
  Baseline,
  flagCount,
  Glyph,
  glyphInset,
  glyphWidth,
  noteGlyph,
  restGlyph,
  rhythmNotationHeight,
} from './rhythm_glyphs.tsx';
import { timeLeft } from './rhythm_layout.tsx';
import { TapGhostStatus, tapNotationMarks } from './rhythm_tap_marks.ts';

export const rhythmTapNotationTestId = 'rhythm-tap-notation';

// The singer's taps as notes against the pattern (issue #3473), on the same time axis as the
// pattern notation so the offset from the expected note is visible: every expected note and rest
// is a faint ghost where RhythmNotation draws it (flags instead of beams, so each note stands
// alone), and each tap that counted is a note at the moment it landed, coloured like its beat -
// green on time, yellow early or late, red for a miss made by extra taps, which are red quarters.
// A beat nobody tapped shows its ghost in red once its band has passed. The row is not a grid:
// marks are absolutely positioned through timeLeft, clamped so the last one stays inside the row.
//
// Live during the tap phase, `tapsMs` are the taps so far and `nowMs` the clock, both relative
// to the tap bar start; on the result screen `nowMs` is omitted. `highlightUpTo` is for a replay:
// the taps after that index (into `tapsMs` in time order) have not sounded yet and are dimmed;
// -1 dims them all.
export const RhythmTapNotation = ({
  pattern,
  level,
  tapsMs,
  nowMs,
  highlightUpTo,
}: {
  pattern: RhythmPattern;
  level: LearnLevel;
  tapsMs: number[];
  nowMs?: number;
  highlightUpTo?: number;
}) => {
  const { ghosts, taps } = tapNotationMarks({ pattern, level, tapsMs, nowMs });
  return (
    <Row data-testid={rhythmTapNotationTestId}>
      <Baseline />
      {ghosts.map((ghost) => (
        <Ghost
          key={ghost.beats} // a ghost per note or rest start, unique in the bar
          style={{ left: markLeft(pattern, ghost.beats) }}
          $status={ghost.status}
          data-ghost=""
          data-value={ghost.value}
          data-status={ghost.status}
        >
          {ghost.rest ? restGlyph(ghost.value) : noteGlyph(ghost.value, flagCount(ghost.value))}
        </Ghost>
      ))}
      {taps.map((tap, index) => (
        <Tap
          key={tap.tapMs} // taps are distinct clock readings
          style={{ left: markLeft(pattern, tap.beats), color: resultColorCss[tap.color] }}
          $dim={highlightUpTo !== undefined && index > highlightUpTo}
          data-tap=""
          data-value={tap.value}
          data-color={tap.color}
          data-kind={tap.kind}
        >
          {noteGlyph(tap.value, flagCount(tap.value))}
        </Tap>
      ))}
    </Row>
  );
};

// The glyph's left edge: its inset past the time point, but never past the row's end
function markLeft(pattern: RhythmPattern, beats: number): string {
  return `min(calc(${timeLeft(pattern, beats)} + ${glyphInset}px), calc(100% - ${glyphWidth}px))`;
}

function ghostOpacity(status: TapGhostStatus): number {
  return status === 'missed' ? 0.55 : 0.3;
}

const Row = styled.div`
  position: relative;
  height: ${rhythmNotationHeight}px;
  color: var(--color-text);
`;

const Ghost = styled(Glyph)<{ $status: TapGhostStatus }>`
  color: ${({ $status }) => ($status === 'missed' ? resultColorCss.red : 'inherit')};
  opacity: ${({ $status }) => ghostOpacity($status)};
`;

const Tap = styled(Glyph)<{ $dim: boolean }>`
  opacity: ${({ $dim }) => ($dim ? 0.25 : 1)};
  transition: opacity 60ms linear;
`;
